import { useStore } from '@nanostores/react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { forwardRef, memo, useCallback, useEffect, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { toast } from 'react-toastify';
import { EditorPanel } from './EditorPanel';
import { Preview, type PreviewRef } from './Preview';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { Slider, type SliderOptions } from '~/components/ui/Slider';
import type { FileChangeCallback } from '~/components/workbench/FileContextMenu';
import { chatStore } from '~/lib/stores/chat';
import { openDevicePreviewURL, useHasDevicePreviewURL } from '~/lib/stores/device-preview';
import { FilesystemError } from '~/lib/stores/files';
import { workbenchStore, type WorkbenchViewType } from '~/lib/stores/workbench';
import { isLaptopOrLarger } from '~/utils/breakpoints';
import { classNames } from '~/utils/classNames';
import { debounce } from '~/utils/debounce';
import { cubicEasingFn } from '~/utils/easings';
import { useInitRef } from '~/utils/init-ref';
import { renderLogger } from '~/utils/logger';
import { removeWorkDir } from '~/utils/workDir';

interface WorkspaceProps {
  open?: boolean;
  previewRef?: RefObject<PreviewRef>;
  login?: () => true | Promise<boolean>;
}

const viewTransition = { ease: cubicEasingFn };

const sliderOptions: SliderOptions<WorkbenchViewType> = {
  left: {
    value: 'code',
    text: 'Code',
  },
  right: {
    value: 'preview',
    text: 'Preview',
  },
};

const setSelectedView = (view: WorkbenchViewType) => {
  workbenchStore.currentView.set(view);
};

const onFileSelect = (filePath: string | undefined) => {
  workbenchStore.setSelectedFile(filePath);
};

const onFileSave = () => {
  workbenchStore.saveCurrentDocument().catch(() => {
    toast.error('Failed to update file content');
  });
};

const onFileReset = () => {
  workbenchStore.resetCurrentDocument();
};

const onFileChange: FileChangeCallback = async (event) => {
  if (event.method === 'rename') {
    return workbenchStore.rename(removeWorkDir(event.oldPath), removeWorkDir(event.newPath));
  }

  if (event.method === 'cut') {
    if (event.oldPath === event.newPath) {
      return undefined;
    }

    return workbenchStore.cut(removeWorkDir(event.oldPath), removeWorkDir(event.newPath), event.type);
  }

  if (event.method === 'copy') {
    const oldPath = removeWorkDir(event.oldPath);
    const newPath = removeWorkDir(event.newPath);

    if (event.type === 'file') {
      const newName = await workbenchStore.copyFile(oldPath, newPath);

      workbenchStore.setSelectedFile(newName);

      return undefined;
    }

    return workbenchStore.copyDirectory(oldPath, newPath);
  }

  const relativePath = removeWorkDir(event.path);

  if (event.type === 'file') {
    switch (event.method) {
      case 'add': {
        try {
          await workbenchStore.writeFile(
            relativePath,
            {
              type: 'file',
              content: '',
              isBinary: false,
              isStackBlitzBinary: false,
            },
            {
              errorIfExists: true,
              errorIfIgnored: true,
            },
          );
        } catch (error) {
          if (!(error instanceof FilesystemError) || error.code !== 'EEXISTS') {
            throw error;
          }
        }

        workbenchStore.setSelectedFile(event.path);

        return undefined;
      }
      case 'remove': {
        return workbenchStore.removeFile(relativePath);
      }
    }
  }

  switch (event.method) {
    case 'add': {
      await workbenchStore.createDirectory(relativePath, { errorIfIgnored: true });
      break;
    }
    case 'remove': {
      return workbenchStore.removeDirectory(relativePath);
    }
  }

  return undefined;
};

const handleToggleChat = () => {
  chatStore.showChat.set(!chatStore.showChat.get());
};

export const Workbench = memo((props: WorkspaceProps) => {
  renderLogger.trace('Workbench');

  const { open, previewRef, login } = props;

  const showChat = useStore(chatStore.showChat);
  const selectedFile = useStore(workbenchStore.selectedFile);
  const currentDocument = useStore(workbenchStore.currentDocument);
  const unsavedFiles = useStore(workbenchStore.unsavedFiles);
  const files = useStore(workbenchStore.files);
  const selectedView = useStore(workbenchStore.currentView);
  const framework = useStore(workbenchStore.framework);
  const isExpo = framework?.name === 'expo';
  const hasDevicePreviewURL = useHasDevicePreviewURL();

  const [sideBySideViewAllowed, setSideBySideViewAllowed] = useState(isLaptopOrLarger());

  const editorPanelNodeRef = useInitRef(() => {
    const div = document.createElement('div');

    /**
     * Sets `display: contents` which causes an element's children to appear as if they were direct children of the
     * element's parent, ignoring the element itself.
     */
    div.classList.add('contents');

    return div;
  });

  const previewPanelNodeRef = useInitRef(() => {
    const div = document.createElement('div');
    div.classList.add('contents');

    return div;
  });

  const editorPanelPortal = createPortal(
    <EditorPanel
      editorDocument={currentDocument}
      selectedFile={selectedFile}
      files={files}
      visible={selectedView === 'code'}
      unsavedFiles={unsavedFiles}
      onFileSelect={onFileSelect}
      onFileSave={onFileSave}
      onFileReset={onFileReset}
      onFileChange={onFileChange}
    />,
    editorPanelNodeRef.current,
  );

  const previewPanelPortal = createPortal(<Preview ref={previewRef} login={login} />, previewPanelNodeRef.current);

  useEffect(() => {
    const handleWindowResize = debounce(() => {
      setSideBySideViewAllowed(isLaptopOrLarger());
    }, 50);

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    workbenchStore.setDocuments(files);
  }, [files]);

  const setPortal = useCallback(
    (ref: HTMLDivElement | null) => {
      if (ref) {
        ref.insertBefore(editorPanelNodeRef.current, null);
      }
    },
    [editorPanelNodeRef],
  );

  const replaceWithPortal = useCallback(
    (ref: HTMLDivElement | null) => {
      if (ref) {
        ref.replaceWith(editorPanelNodeRef.current);
      }
    },
    [editorPanelNodeRef],
  );

  const setPreviewPortal = (ref: HTMLDivElement | null) => {
    if (ref) {
      ref.insertBefore(previewPanelNodeRef.current, null);
    }
  };

  const replaceWithPreviewPortal = (ref: HTMLDivElement | null) => {
    if (ref) {
      ref.replaceWith(previewPanelNodeRef.current);
    }
  };

  const showPanels = showChat || !sideBySideViewAllowed;

  return (
    open && (
      <>
        {editorPanelPortal}
        {previewPanelPortal}
        <div className="z-workbench w-[var(--workbench-width)]">
          <div
            className={classNames(
              'fixed top-[calc(var(--header-height)+1rem)] bottom-4 w-[var(--workbench-inner-width)] z-0 left-[var(--workbench-left)]',
            )}
          >
            <div className="select-none absolute inset-0">
              <div className="h-full flex flex-col bg-bolt-elements-background-depth-2 border border-bolt-elements-borderColor shadow-sm rounded-lg overflow-hidden">
                <div className="flex items-center px-3 py-2 border-b border-bolt-elements-borderColor min-h-[var(--panel-header-height)]">
                  <LegacyIconButton
                    icon={showChat ? 'i-ph:sidebar-simple-fill' : 'i-ph:sidebar-simple'}
                    className="mr-1.5"
                    size="lg"
                    title={showChat ? 'Hide chat' : 'Show chat'}
                    onClick={handleToggleChat}
                  />
                  {showPanels && (
                    <>
                      <Slider selected={selectedView} options={sliderOptions} setSelected={setSelectedView} />
                      {isExpo && selectedView === 'preview' && (
                        <LegacyButton
                          className="ml-auto text-bolt-elements-item-contentDefault bg-transparent enabled:hover:text-bolt-elements-item-contentActive rounded-md enabled:hover:bg-bolt-elements-item-backgroundActive"
                          variant="secondary"
                          disabled={!hasDevicePreviewURL}
                          onClick={openDevicePreviewURL}
                        >
                          <span className="i-ph:qr-code scale-140 mr-2" />
                          Device Preview
                        </LegacyButton>
                      )}
                    </>
                  )}
                </div>

                {showPanels ? (
                  <div className="relative flex-1 overflow-hidden">
                    <View
                      ref={setPortal}
                      initial={{ x: selectedView === 'code' ? 0 : '-100%' }}
                      animate={{ x: selectedView === 'code' ? 0 : '-100%' }}
                    />
                    <View
                      ref={setPreviewPortal}
                      initial={{ x: selectedView === 'preview' ? 0 : '100%' }}
                      animate={{ x: selectedView === 'preview' ? 0 : '100%' }}
                    />
                  </div>
                ) : (
                  <PanelGroup className="flex h-full" direction="horizontal">
                    <Panel className="border-r border-bolt-elements-borderColor w-full" defaultSize={50}>
                      <div ref={replaceWithPortal} />
                    </Panel>
                    <PanelResizeHandle />
                    <Panel className="w-full" defaultSize={50}>
                      <div ref={replaceWithPreviewPortal} />
                    </Panel>
                  </PanelGroup>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    )
  );
});

Workbench.displayName = 'Workbench';

interface ViewProps extends HTMLMotionProps<'div'> {
  children?: JSX.Element;
}

const View = forwardRef<HTMLDivElement, ViewProps>(({ children, ...props }, ref) => {
  return (
    <motion.div ref={ref} className="absolute inset-0" transition={viewTransition} {...props}>
      {children}
    </motion.div>
  );
});
